/**
 * S3FileWidget component.
 * @module components/manage/Widgets/S3FileWidget
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useState, createContext, useContext } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Button, Checkbox, Dimmer } from 'semantic-ui-react';
import { injectIntl } from 'react-intl';
import attachmentSVG from '../../../../icons/attach_file.svg';
import { Icon, FormFieldWrapper, Toast } from '@plone/volto/components';
import loadable from '@loadable/component';
import { validateFileUploadSize } from '@plone/volto/helpers';
import { defineMessages, useIntl } from 'react-intl';
import config from '@plone/volto/registry';
import request from 'superagent';
import { toast } from 'react-toastify';

const Dropzone = loadable(() => import('react-dropzone'));

const SKIP_VALUE = 'NO_FILE';

const messages = defineMessages({
  releaseDrag: {
    id: 'Drop the file here ...',
    defaultMessage: 'Drop the file here ...',
  },
  fileDrag: {
    id: 'Drag and drop your file here or',
    defaultMessage: 'Drag and drop your file here or',
  },
  chooseFile: {
    id: 'choose the file to upload',
    defaultMessage: 'choose the file to upload',
  },
  noFile: {
    id: "I don't attach a file for this",
    defaultMessage: "I don't attach a file for this",
  },
  downloadFile: {
    id: 'Download',
    defaultMessage: 'Download',
  },
  deleteFile: {
    id: 'Delete',
    defaultMessage: 'Delete',
  },
  replaceFile: {
    id: 'Replace',
    defaultMessage: 'Replace',
  },
  uploadError: {
    id: 'S3UploadError',
    defaultMessage: 'Upload failed',
  },
  uploadErrorHelp: {
    id: 'S3UploadErrorHelp',
    defaultMessage:
      'You may try again later, or with a different device or browser.',
  },
  saving: {
    id: 'Saving',
    defaultMessage: 'Saving…',
  },
  writeProtected: {
    id: 'Error. The file is write protected.',
    defaultMessage: 'Error. The file is write protected.',
  },
});

/**
 * S3FileWidget component class.
 * @function S3FileWidget
 * @returns {string} Markup of the component.
 *
 * To use it, in schema properties, declare a field like:
 *
 * ```jsx
 * {
 *  title: "File",
 *  widget: 's3file',
 * }
 * ```
 * or:
 *
 * ```jsx
 * {
 *  title: "File",
 *  type: 'string',
 * }
 * ```
 *
 */
const S3FileWidget = (props) => {
  const { id, value, onChange, required } = props;
  const [S3Prefix, setS3Prefix] = useState(null); // null, string
  const [state, setState] = useState(value); // null, string
  const [uploading, setUploading] = useState(false);
  const replaceOnly = !!useContext(S3FileWidgetContext)?.replaceOnly;
  const isDisabled =
    !!useContext(S3FileWidgetContext)?.isDisabled || !!props.isDisabled;
  const isPreview = !!useContext(S3FileWidgetContext)?.isPreview;
  const data = useSelector((state) => state.content.data, shallowEqual);
  const intl = useIntl();

  // S3 can only be used when the object has been persisted with UID
  React.useEffect(() => {
    const uid = data?.UID || null;
    if (!S3Prefix && uid) {
      setS3Prefix(`${config.settings.S3ApiPath}/${uid}/${id}`);
    }
  }, [id, data, S3Prefix]);

  // S3 can only be used when the object has been persisted with UID
  React.useEffect(() => {
    if (S3Prefix && !isPreview) {
      (async () => {
        try {
          const res = await request.get(`${S3Prefix}/HEAD`);
          if (res.status === 200) {
            setState(res.header['x-ilpo-meta-filename']);
          }
        } catch (e) {
          if (e.status === 404) {
            setState(null);
          } else {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      })();
    }
  }, [S3Prefix, isPreview]);

  React.useEffect(() => {
    if (id && value !== state) {
      if (state === SKIP_VALUE || (value === SKIP_VALUE && !state)) {
        setState(value);
      } else {
        onChange(id, state);
      }
    }
  }, [id, value, state, onChange]);

  const onDrop = async (files) => {
    const file = files[0];
    if (!validateFileUploadSize(file, intl.formatMessage)) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const fileContents = new Uint8Array(reader.result);
      // eslint-disable-line no-control-regex
      const validName = file.name
        .replace(/[^\x20-\x7E]/g, '-') // replace all non-visible characters with hyphens
        .replace(/\s+/g, '_'); // remove non-ASCII characters and replace white spaces with underscores
      // Note: Alternatively, we could base64 encode the file name, but because file names will
      // be used in URLs, normalizing them to ASCII seems like a better option.
      setUploading(true);
      try {
        let res = await request
          .put(`${S3Prefix}/HEAD`)
          .set('Content-Type', file.type)
          .set('x-ilpo-meta-filename', validName)
          .send();
        if (res.statusCode === 204 && !!res.headers.location) {
          await request
            .put(res.headers.location)
            .set('Content-Type', file.type)
            .set('x-ilpo-meta-filename', validName)
            .send(fileContents);
          try {
            res = await request.get(`${S3Prefix}/HEAD`);
            if (res.status === 200) {
              setState(res.header['x-ilpo-meta-filename']);
            }
          } catch (e) {
            toast.error(
              <Toast
                error
                title={intl.formatMessage(messages.uploadError)}
                content={intl.formatMessage(messages.uploadErrorHelp)}
              />,
            );
            if (e.status === 404) {
              setState(null);
            } else {
              // eslint-disable-next-line no-console
              console.error(e);
            }
          }
        }
        setUploading(false);
      } catch (e) {
        setUploading(false);
        if (e.status === 403) {
          toast.error(
            <Toast error title={intl.formatMessage(messages.writeProtected)} />,
          );
        }
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <FormFieldWrapper {...props}>
      {!uploading && (!state || (state === SKIP_VALUE && !required)) && (
        <Dropzone onDrop={onDrop} disabled={!S3Prefix}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div className="s3-file-widget-dropzone" {...getRootProps()}>
              {isDragActive && <Dimmer active></Dimmer>}
              {
                <div className="dropzone-placeholder">
                  {isDragActive ? (
                    <p className="dropzone-text">
                      {intl.formatMessage(messages.releaseDrag)}
                    </p>
                  ) : (
                    <p className="dropzone-text">
                      {intl.formatMessage(messages.fileDrag)}{' '}
                      <label className="label-file-widget-input">
                        {intl.formatMessage(messages.chooseFile)}
                      </label>
                    </p>
                  )}
                  {required && (
                    <Checkbox
                      className="s3-file-no-file-checkbox"
                      name={`field-${id}-no-file`}
                      checked={false}
                      disabled={isDisabled}
                      onKeyPress={(event) => {
                        if (event.keyCode === 32 || event.which === 32) {
                          event.stopPropagation();
                          onChange(id, SKIP_VALUE);
                          setTimeout(
                            () =>
                              document
                                .querySelector(
                                  `input[name="field-${id}-no-file"]`,
                                )
                                ?.focus(),
                            0,
                          );
                        }
                      }}
                      onChange={(event, { checked }) => {
                        event.stopPropagation();
                        onChange(id, SKIP_VALUE);
                        setTimeout(
                          () =>
                            document
                              .querySelector(
                                `input[name="field-${id}-no-file"]`,
                              )
                              ?.focus(),
                          0,
                        );
                      }}
                      label={
                        <label htmlFor={`field-${id}-no-file`}>
                          {intl.formatMessage(messages.noFile)}
                        </label>
                      }
                    />
                  )}
                </div>
              }
              <input
                {...getInputProps({
                  type: 'file',
                  style: { display: 'none' },
                })}
                id={`field-${id}`}
                name={id}
                type="file"
                disabled={isDisabled}
              />
            </div>
          )}
        </Dropzone>
      )}
      {!uploading && (!state || state === SKIP_VALUE) ? (
        <div className="field-file-name" />
      ) : (
        <div className="field-file-name s3-file-has-file-container">
          <Icon name={attachmentSVG} size="46px" />
          {uploading ? intl.formatMessage(messages.saving) : state}
          {
            <div className="s3-file-actions-container">
              <Button
                icon
                basic
                className="download-button"
                disabled={uploading}
                onClick={() => {
                  window.location = state
                    ? `${S3Prefix}?filename=${state}`
                    : S3Prefix;
                }}
              >
                {intl.formatMessage(messages.downloadFile)}
              </Button>
              {isDisabled ? null : replaceOnly ? (
                <span>
                  <label
                    className="ui basic button delete-button"
                    htmlFor={`field-${id}`}
                  >
                    {intl.formatMessage(messages.replaceFile)}
                  </label>
                  <input
                    style={{ display: 'none' }}
                    id={`field-${id}`}
                    name={id}
                    type="file"
                    disabled={isDisabled}
                    onChange={(event) => {
                      onDrop(event.target.files);
                    }}
                  />
                </span>
              ) : (
                <Button
                  icon
                  basic
                  className="delete-button"
                  disabled={isDisabled || uploading}
                  onClick={async () => {
                    try {
                      const response = await request.delete(S3Prefix);
                      if (response.status === 204) {
                        setState(null);
                      }
                    } catch (e) {
                      if (e.status === 403) {
                        toast.error(
                          <Toast
                            error
                            title={intl.formatMessage(messages.writeProtected)}
                          />,
                        );
                      }
                      // eslint-disable-next-line no-console
                      console.error(e);
                    }
                  }}
                >
                  {intl.formatMessage(messages.deleteFile)}
                </Button>
              )}
            </div>
          }
        </div>
      )}
      {required && state === SKIP_VALUE && (
        <div className="s3-file-widget-dropzone s3-file-widget-disabled">
          <div className="dropzone-placeholder">
            <p className="dropzone-text">
              {intl.formatMessage(messages.fileDrag)}{' '}
              {intl.formatMessage(messages.chooseFile)}
            </p>
            <Checkbox
              className="s3-file-no-file-checkbox"
              name={`field-${id}-no-file`}
              checked={true}
              disabled={isDisabled}
              onChange={(event, { checked }) => {
                onChange(id, null);
                setTimeout(
                  () =>
                    document
                      .querySelector(`input[name="field-${id}-no-file"]`)
                      ?.focus(),
                  0,
                );
              }}
              label={
                <label htmlFor={`field-${id}-no-file`}>
                  {intl.formatMessage(messages.noFile)}
                </label>
              }
            />
          </div>
        </div>
      )}
    </FormFieldWrapper>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
S3FileWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      '@type': PropTypes.string,
      title: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  wrapped: PropTypes.bool,
};

/**
 * Default properties.
 * @property {Object} defaultProps Default properties.
 * @static
 */
S3FileWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
};

export default injectIntl(S3FileWidget);

export const S3FileWidgetContext = createContext({
  isDisabled: false,
  isPreview: false,
  replaceOnly: false,
});
